/**
 * Documentation home page
 */

import {Link} from 'react-router-dom';
import {DocsContent} from '../../components/docs/DocsContent';
import {DocsActionButtons} from '../../components/docs/DocsActionButtons';

export function DocsHome() {
  return (
    <DocsContent
      title="VoxaPilot Documentation"
      description="VoxaPilot is an AI-powered meeting intelligence platform that understands your meetings and helps you work faster through real-time transcription, intelligent summaries, and actionable insights. Just record your meetings and VoxaPilot will generate transcripts, summaries, and key insights for you."
    >

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Link
          to="/docs/quickstart"
          className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all bg-white dark:bg-[#252526]"
        >
          <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">Quickstart</h3>
          <p className="text-textSecondary dark:text-[#858585]">
            Get up and running with VoxaPilot in minutes. Learn how to record your first meeting
            and generate transcripts.
          </p>
        </Link>

        <Link
          to="/docs/concepts"
          className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all bg-white dark:bg-[#252526]"
        >
          <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">Core Concepts</h3>
          <p className="text-textSecondary dark:text-[#858585]">
            Understand how VoxaPilot works, including transcription, AI summaries, and speaker
            detection.
          </p>
        </Link>

        <Link
          to="/docs/features/transcription"
          className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all bg-white dark:bg-[#252526]"
        >
          <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">Features</h3>
          <p className="text-textSecondary dark:text-[#858585]">
            Explore all of VoxaPilot's features including real-time transcription, AI summaries,
            and analytics.
          </p>
        </Link>

        <Link
          to="/docs/api/overview"
          className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all bg-white dark:bg-[#252526]"
        >
          <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">API Reference</h3>
          <p className="text-textSecondary dark:text-[#858585]">
            Integrate VoxaPilot into your application using our REST API. Full documentation and
            examples included.
          </p>
        </Link>
      </div>

      {/* Getting Started Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-12">
        <h2 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-6">Getting Started</h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">1. Create an Account</h3>
            <p className="text-textSecondary dark:text-[#858585]">
              Sign up for a free VoxaPilot account to get started. You'll get 10 hours of
              transcription per month on the free plan.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">2. Record Your First Meeting</h3>
            <p className="text-textSecondary dark:text-[#858585]">
              Connect your meeting platform (Zoom, Google Meet, Teams) or upload an audio file.
              VoxaPilot will automatically transcribe your meeting.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">3. Review Transcripts & Summaries</h3>
            <p className="text-textSecondary dark:text-[#858585]">
              Access your transcripts, AI-generated summaries, action items, and insights from the
              dashboard. Search through all your meetings instantly.
            </p>
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/docs/quickstart"
            className="inline-block px-6 py-3 bg-gray-900 dark:bg-[#1E1E1E] text-white font-medium rounded-md hover:bg-gray-800 dark:hover:bg-[#2D2D30] transition-colors"
          >
            Start the Quickstart Guide →
          </Link>
        </div>
      </div>

      <DocsActionButtons pageTitle="VoxaPilot Documentation" />
    </DocsContent>
  );
}

